/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import { CalendarClock, Clock3, Radio } from "lucide-react";
import { MediaModel } from "@/database";

interface Props {
  media: MediaModel;
}

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const FALLBACK_LIVE_WINDOW = 6 * HOUR;

type Phase = "far" | "soon" | "imminent" | "live";

export function isPremiereChipVisible(media: MediaModel): boolean {
  const isPremiere =
    media?.asset_type === "premiere" || media?.media_ref?.rel === "premiere";
  const start = Number(media?.media_schedule || 0);
  if (!isPremiere || !start) return false;
  const durationS = Number(media?.meta?.duration || 0);
  const loops = Math.max(1, Number(media?.loop_count || 1));
  const liveWindowMs =
    durationS > 0 ? durationS * loops * 1000 : FALLBACK_LIVE_WINDOW;
  const end = start + liveWindowMs;
  return Date.now() < end;
}

function formatDelta(ms: number): string {
  if (ms < MINUTE) {
    const s = Math.max(0, Math.floor(ms / 1000));
    return `${s.toString().padStart(2, "0")}s`;
  }
  if (ms < HOUR) {
    const m = Math.floor(ms / MINUTE);
    const s = Math.floor((ms % MINUTE) / 1000);
    return `${m}:${s.toString().padStart(2, "0")}`;
  }
  if (ms < DAY) {
    const h = Math.floor(ms / HOUR);
    const m = Math.floor((ms % HOUR) / MINUTE);
    return `${h}h ${m}m`;
  }
  const d = Math.floor(ms / DAY);
  const h = Math.floor((ms % DAY) / HOUR);
  return `${d}d ${h}h`;
}

function formatAbsolute(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const dateStr = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const timeStr = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${dateStr} · ${timeStr}`;
}

export const RenderPremiereSchedule: React.FC<Props> = ({ media }) => {
  const isPremiere =
    media?.asset_type === "premiere" ||
    media?.media_ref?.rel === "premiere";

  const start = Number(media?.media_schedule || 0);
  const durationS = Number(media?.meta?.duration || 0);
  const loops = Math.max(1, Number(media?.loop_count || 1));
  const liveWindowMs = durationS > 0 ? durationS * loops * 1000 : FALLBACK_LIVE_WINDOW;
  const end = start > 0 ? start + liveWindowMs : 0;

  const [now, setNow] = useState(Date.now());

  const phase: Phase | null = useMemo(() => {
    if (!isPremiere || !start) return null;
    if (now >= end) return null;
    if (now >= start) return "live";
    const delta = start - now;
    if (delta <= 5 * MINUTE) return "imminent";
    if (delta <= DAY) return "soon";
    return "far";
  }, [isPremiere, start, end, now]);

  useEffect(() => {
    if (!phase) return;
    const tick = phase === "imminent" || phase === "live" ? 1000 : 30 * 1000;
    const id = setInterval(() => setNow(Date.now()), tick);
    return () => clearInterval(id);
  }, [phase]);

  if (!phase) return null;

  const delta = start - now;

  let label: string;
  let Icon: React.ElementType;
  let palette: { bg: string; text: string; border: string };
  let pulse = false;

  if (phase === "live") {
    label = "LIVE";
    Icon = Radio;
    palette = { bg: "#fdecec", text: "#b42318", border: "#f4b9b9" };
    pulse = true;
  } else if (phase === "imminent") {
    label = `Live in ${formatDelta(Math.max(0, delta))}`;
    Icon = Clock3;
    palette = { bg: "#fdecec", text: "#b42318", border: "#f4b9b9" };
    pulse = true;
  } else if (phase === "soon") {
    label = `Live in ${formatDelta(Math.max(0, delta))}`;
    Icon = Clock3;
    palette = { bg: "#fff5e6", text: "#9a5a00", border: "#f6dcae" };
  } else {
    label = formatAbsolute(start);
    Icon = CalendarClock;
    palette = { bg: "#eef2f7", text: "#475569", border: "#dbe2eb" };
  }

  return (
    <div
      className="d-inline-flex align-items-center gap-1 mb-2"
      style={{
        backgroundColor: palette.bg,
        color: palette.text,
        border: `1px solid ${palette.border}`,
        borderRadius: 8,
        padding: "3px 10px",
        fontSize: "12px",
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
      }}
      title={
        phase === "live"
          ? `Started ${formatAbsolute(start)}`
          : `Scheduled ${formatAbsolute(start)}`
      }
    >
      {pulse ? (
        <span
          aria-hidden
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            backgroundColor: palette.text,
            display: "inline-block",
            animation: "premiere-pulse 1.2s ease-in-out infinite",
          }}
        />
      ) : (
        <Icon size={13} strokeWidth={2.2} />
      )}
      <span>{label}</span>
      <style>{`
        @keyframes premiere-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.45; transform: scale(0.8); }
        }
      `}</style>
    </div>
  );
};

export default RenderPremiereSchedule;
